/** @jsxImportSource theme-ui */
import React from 'react';
import { Box, Flex, SearchInput, Button, Text, useBreakpointIndex } from '@infotrack/zenith-ui';
import {
  headerContainerStyles,
  leftSectionStyles,
  filtersSectionStyles,
  searchInputWrapperStyles,
  filterToggleStyles,
  resultsTextStyles,
  rightSectionStyles,
  mobileTopRowStyles,
  mobileFiltersRowStyles,
} from './staffHeader.styles';

interface StaffHeaderProps {
  resultCount: number;
  searchText: string;
  onSearchChange: (text: string) => void;
  onSearchClear: () => void;
  includeInactive: boolean;
  onIncludeInactiveChange: (include: boolean) => void;
  hasAdminAccess: boolean;
  onAddStaff?: () => void;
  onDownloadCSV?: () => void;
}

export const StaffHeader: React.FC<StaffHeaderProps> = ({
  resultCount,
  searchText,
  onSearchChange,
  onSearchClear,
  includeInactive,
  onIncludeInactiveChange,
  hasAdminAccess,
  onAddStaff,
  onDownloadCSV,
}) => {
  const breakpointIndex = useBreakpointIndex();
  const isDesktop = breakpointIndex >= 2;

  const title = (
    <Text variant="hero" color="primary/600">
      Staff Management
    </Text>
  );

  const searchControls = (
    <>
      <Box sx={searchInputWrapperStyles}>
        <SearchInput
          placeholder="Search by name or email..."
          value={searchText}
          onSearch={onSearchChange}
          onClear={onSearchClear}
          debounce={300}
          aria-label="Search staff"
          size="small"
        />
      </Box>
      <Box sx={filterToggleStyles}>
        <label>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => onIncludeInactiveChange(e.target.checked)}
          />
          <span style={{ marginLeft: '0.5rem' }}>Include Inactive</span>
        </label>
      </Box>
    </>
  );

  const resultsText = (
    <Text variant="caption">
      {isDesktop ? 'Displaying ' : ''}
      {resultCount} {resultCount === 1 ? 'staff member' : 'staff members'}
    </Text>
  );

  const addButton = hasAdminAccess && onAddStaff && (
    <Button
      variant="primary"
      size="small"
      onClick={onAddStaff}
      aria-label="Add new staff member"
    >
      Add Staff
    </Button>
  );

  // Desktop layout - single row
  if (isDesktop) {
    return (
      <Flex role="banner" sx={headerContainerStyles}>
        <Flex sx={leftSectionStyles}>
          {title}
          <Flex
            sx={filtersSectionStyles}
            role="toolbar"
            aria-label="Staff filters"
          >
            {searchControls}
          </Flex>
          <Box role="status" aria-live="polite" sx={resultsTextStyles}>
            {resultsText}
          </Box>
        </Flex>
        <Flex sx={rightSectionStyles}>
          {onDownloadCSV && (
            <Button
              variant="secondary"
              size="small"
              onClick={onDownloadCSV}
              aria-label="Download staff list as CSV"
            >
              Download CSV
            </Button>
          )}
          {addButton}
        </Flex>
      </Flex>
    );
  }

  // Mobile layout - multiple rows
  return (
    <Flex role="banner" sx={headerContainerStyles}>
      <Flex sx={mobileTopRowStyles}>
        {title}
        <Flex sx={{ gap: 2 }}>
          {onDownloadCSV && (
            <Button
              variant="secondary"
              size="small"
              onClick={onDownloadCSV}
              aria-label="Download staff list as CSV"
            >
              Download CSV
            </Button>
          )}
          {addButton}
        </Flex>
      </Flex>
      <Flex sx={mobileFiltersRowStyles}>
        {searchControls}
      </Flex>
      <Flex role="status" aria-live="polite" sx={resultsTextStyles}>
        {resultsText}
      </Flex>
    </Flex>
  );
};
